import { useMemo, useState } from "react";
import RightSideSheet, { FILTER_SHEET_WIDTHS } from "@/components/ui/others/RightSideSheet";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import {
  setIsAssign,
  setCurrentLeadId,
} from "@/features/leads/stores/leadFunctionalSlice";
import { Button } from "@/components/ui/button";
import SearchInput from "@/components/ui/searchInput";
import { FilterButton } from "@/components/ui/FilterButton";
import FilterSection, {
  DEFAULT_ASSIGN_FILTERS,
  type AssignFilters,
} from "../../assign-lead/components/FilterSection";
import RolesList from "../../assign-lead/components/RolesList";
import UsersList from "../../assign-lead/components/UsersList";
import { useLeadMutations } from "@/features/leads/api/useLeadMutations";
import { useChildMutations } from "@/features/leads/api/useChildMutations";
import { useLeadForm } from "@/features/leads/contexts/FormLeadContext";
import { useChildForm } from "@/features/leads/contexts/ChildProvider";
import { toast } from "sonner";
import useAssignEligibility from "@/features/leads/api/useAssignEligibility";
import { fetchAssignPreview } from "@/features/leads/api/useAssignPreview";
import type { AssignPreviewPayload } from "@/features/leads/types/assignPreview";

type AutoReplacePreview = Extract<
  AssignPreviewPayload,
  { mode: "auto-lead" | "auto-child" }
>;
import AssignLevelPopup from "./AssignLevelPopup";
import CrossLevelSwapConfirmModal from "./CrossLevelSwapConfirmModal";
import SameRoleReplaceConfirmModal from "./SameRoleReplaceConfirmModal";
import PeerPickerList from "./PeerPickerList";
import { useAssignationPermissions } from "@/features/leads/hooks/useAssignationPermissions";
import usePeerEligibility from "@/features/leads/api/usePeerEligibility";
import { userHoldsSlot } from "@/features/leads/utils/holdsSlot";
import { selectCurrentUserRecord } from "@/features/auth/currentUser";

/** `selectedRole` sentinel for the "your peers" row (never a real role id). */
const PEER_ROLE = "__peers__";

export default function AssignUserSheet() {
  const isAssign = useAppSelector((state) => state.leadFunctional.isAssign);
  const selectedUser = useAppSelector(
    (state) => state.leadFunctional.selectedUser
  );
  const dispatch = useAppDispatch();
  const { lead } = useLeadForm();
  const { currentChildId, child: activeChild } = useChildForm();

  const [selectedRole, setSelectedRole] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [filters, setFilters] = useState<AssignFilters>(DEFAULT_ASSIGN_FILTERS);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [askPayload, setAskPayload] = useState<
    Extract<AssignPreviewPayload, { mode: "ask" }> | null
  >(null);
  const [levelPopupOpen, setLevelPopupOpen] = useState(false);
  const [crossModalOpen, setCrossModalOpen] = useState(false);
  const [sameRoleModalOpen, setSameRoleModalOpen] = useState(false);
  const [sameRolePreview, setSameRolePreview] = useState<AutoReplacePreview | null>(
    null,
  );
  // Cross-level confirm items — from the level popup's "Lead user" pick or a
  // lead-user role's preview (no popup).
  const [crossItems, setCrossItems] = useState<
    { childId: number; childLabel: string; peerName: string }[]
  >([]);
  // "Deal user" role replacing same-role holders on the deals it lands on.
  const [dealUserPreview, setDealUserPreview] = useState<
    Extract<AssignPreviewPayload, { mode: "holder-child" }> | null
  >(null);

  const { assignLeads, peerTransfer } = useLeadMutations();
  const { assignChild } = useChildMutations();

  const isChildActive = !!currentChildId;
  const childIdNum =
    isChildActive &&
    currentChildId != null &&
    Number.isFinite(Number(currentChildId))
      ? Number(currentChildId)
      : null;

  // "Transfer to peers" inside the full drawer. The role pipeline never lists
  // the viewer's OWN role (a non-admin only sees roles below theirs), so a
  // role holding BOTH "Outbound — assign leads" and "Transfer to peers" used
  // to open this drawer and find nobody at all (Adi's counsellors,
  // 2026-10-28) — the peer-only sheet opens only when full outbound is off.
  // Their peers now ride as the top row; picking it lists the server's
  // eligible same-role peers, and "Assign" becomes the self-revoking
  // hand-over the peer-only sheet performs (same endpoint, same rules).
  const { canOutboundPeer } = useAssignationPermissions();
  // Through the accessor: after a reload (every "Switch user" session) the
  // slice holds `{ user, token }`, and a bare `currentUser.id` is undefined.
  const currentUser = useAppSelector(selectCurrentUserRecord);
  const iHoldSlot = userHoldsSlot({
    lead,
    activeChild,
    isChildActive,
    userId: currentUser?.id,
  });
  const peerAvailable = canOutboundPeer && iHoldSlot;
  const peerMode = selectedRole === PEER_ROLE;
  const [selectedPeerId, setSelectedPeerId] = useState<number | null>(null);
  const {
    peers,
    isLoading: peersLoading,
    isError: peersError,
    errorMessage: peersErrorMessage,
  } = usePeerEligibility(lead?.id ?? null, childIdNum, isAssign && peerAvailable);
  const selectedPeer = peers.find((p) => p.id === selectedPeerId) ?? null;
  const peerRoleName: string =
    currentUser?.role?.name ??
    currentUser?.roleName ??
    peers[0]?.role ??
    "Your role";

  // Single-lead and child assignments share the lead's branch (children
  // inherit the parent lead's branch).
  //
  // Child-level assign is narrow-to-slot: gates come ONLY from the active
  // deal's own CFs. We never merge in the parent lead's gates, because
  // a lenient lead-level value (e.g. country = "Germany, Ireland, Canada")
  // would let a France-only user through on a Canada-only deal.
  // See `feedback-cf-block-scope-narrow`.
  const assignGateFromLead = useMemo(() => {
    if (!lead?.id) return null;
    type Gate = { customInputId: number; itemId: number };
    if (!isChildActive) {
      const leadPerms: number[] =
        (lead as { permissionItemIds?: number[] }).permissionItemIds ?? [];
      const leadGates: Gate[] =
        (lead as { permissionGates?: Gate[] }).permissionGates ?? [];
      return {
        branchId: lead.branchId ?? null,
        permissionItemIds: leadPerms,
        permissionGates: leadGates,
      };
    }
    const childRecord =
      (activeChild as { permissionItemIds?: number[]; permissionGates?: Gate[] } | null | undefined) ??
      (Array.isArray((lead as { child?: any[] }).child)
        ? (lead as { child?: any[] }).child!.find(
            (c: any) => Number(c?.id) === Number(currentChildId),
          )
        : undefined);
    return {
      branchId: lead.branchId ?? null,
      permissionItemIds: childRecord?.permissionItemIds ?? [],
      permissionGates: childRecord?.permissionGates ?? [],
    };
  }, [lead, isChildActive, activeChild, currentChildId]);

  const selectedIdsForEligibility = useMemo(
    () => (!isChildActive && lead?.id ? [lead.id] : []),
    [isChildActive, lead?.id],
  );
  const { totalLeads, eligibility } = useAssignEligibility(
    selectedIdsForEligibility,
  );

  const closeAllModals = () => {
    setLevelPopupOpen(false);
    setCrossModalOpen(false);
    setSameRoleModalOpen(false);
    setSameRolePreview(null);
    setAskPayload(null);
    setCrossItems([]);
    setDealUserPreview(null);
  };

  const handleClose = () => {
    closeAllModals();
    dispatch(setIsAssign(false));
    setSelectedRole("");
    setSearchTerm("");
    setShowFilter(false);
    setFilters(DEFAULT_ASSIGN_FILTERS);
    setSelectedPeerId(null);
  };

  const handlePeerTransfer = () => {
    if (!lead?.id || selectedPeerId == null) return;
    peerTransfer.mutate(
      { leadId: lead.id, toUserId: selectedPeerId, childId: childIdNum },
      {
        onSuccess: () => {
          handleClose();
          // Handed off and access lost — close the detail drawer, exactly as
          // the peer-only sheet does. The board bust drops it from the list.
          dispatch(setCurrentLeadId(null));
        },
      },
    );
  };

  const runAssignChild = (childId: number) => runAssignChildren([childId]);

  const runAssignChildren = (childIds: number[]) => {
    if (!selectedUser || childIds.length === 0) return;
    assignChild.mutate(
      { childIds, userId: selectedUser, level: "child" },
      {
        // The assignChild hook already refreshes the drawer + board.
        onSuccess: () => {
          handleClose();
        },
      },
    );
  };

  const runAssignLead = (confirmCrossRevoke: boolean) => {
    if (!selectedUser || !lead?.id) return;
    assignLeads.mutate(
      {
        selectedLeadIds: [lead.id],
        selectedUser,
        level: "lead",
        confirmCrossRevoke,
      },
      {
        // The assignLeads hook already refreshes the drawer + board.
        onSuccess: () => {
          handleClose();
        },
      },
    );
  };

  const handleAssign = async () => {
    if (!selectedUser || !lead?.id) return;
    setPreviewLoading(true);
    try {
      const activeChildIdNum =
        isChildActive && currentChildId != null
          ? Number(currentChildId)
          : null;
      const p = await fetchAssignPreview(
        lead.id,
        selectedUser,
        activeChildIdNum != null && Number.isFinite(activeChildIdNum)
          ? activeChildIdNum
          : null,
      );

      if (p.mode === "auto-lead" || p.mode === "auto-child") {
        setSameRolePreview(p);
        setSameRoleModalOpen(true);
        return;
      }
      if (p.mode === "ask") {
        setAskPayload(p);
        setLevelPopupOpen(true);
        return;
      }
      // "Lead user / Deal user": the role holds at one level, so there is
      // no lead-or-deal question — assign straight at that level.
      if (p.mode === "holder-blocked") {
        toast.error(p.reason);
        return;
      }
      if (p.mode === "holder-lead") {
        if (p.crossLevelOnLeadPick.length > 0) {
          setCrossItems(p.crossLevelOnLeadPick);
          setCrossModalOpen(true);
        } else {
          runAssignLead(false);
        }
        return;
      }
      if (p.mode === "holder-child") {
        if (p.replacing.length > 0 || p.replacingLeadHolder) {
          setDealUserPreview(p);
        } else {
          runAssignChildren(p.childIds);
        }
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      const msg = err?.response?.data?.message || "Could not start assignment";
      toast.error(msg);
    } finally {
      setPreviewLoading(false);
    }
  };

  const onLevelPopupChooseLead = () => {
    if (!askPayload) return;
    if (askPayload.crossLevelOnLeadPick.length > 0) {
      setLevelPopupOpen(false);
      setCrossItems(askPayload.crossLevelOnLeadPick);
      setCrossModalOpen(true);
    } else {
      setLevelPopupOpen(false);
      setAskPayload(null);
      runAssignLead(false);
    }
  };

  const onCrossModalConfirm = () => {
    runAssignLead(true);
    closeAllModals();
  };

  // Same-role replace: the server resets the role's follow-up to today on
  // every hand-over (no keep/fresh choice), the dialog only confirms.
  const onSameRoleReplaceConfirm = () => {
    if (!sameRolePreview) return;
    if (sameRolePreview.mode === "auto-lead") {
      runAssignLead(false);
    } else {
      runAssignChild(sameRolePreview.childId);
    }
    closeAllModals();
  };

  const onLevelPopupChooseChild = (childId: number) => {
    runAssignChild(childId);
    closeAllModals();
  };

  const isBusy =
    previewLoading ||
    assignLeads.isPending ||
    assignChild.isPending ||
    peerTransfer.isPending;

  return (
    <>
      <RightSideSheet
        widths={FILTER_SHEET_WIDTHS}
        open={isAssign}
        handleClose={handleClose}
        title={isChildActive ? "Assign User to Application" : "Assign User"}
        description={
          peerMode
            ? "Hand this off to another user on your role. They must pass the same branch + custom-field rules; you lose access once transferred."
            : "Select a role, then pick a user. Placement follows existing assignments when possible."
        }
        handleBack={() => {
          setSelectedRole("");
          setSearchTerm("");
          setSelectedPeerId(null);
        }}
        backIcon={!!selectedRole}
        actions={
          <div className="flex items-center justify-end gap-2">
            <Button variant="cancel" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={peerMode ? handlePeerTransfer : handleAssign}
              disabled={
                (peerMode ? selectedPeerId == null : !selectedUser) || isBusy
              }
            >
              {isBusy
                ? peerMode
                  ? "Transferring…"
                  : "Assigning…"
                : peerMode
                  ? selectedPeer
                    ? `Hand over to ${selectedPeer.name ?? "peer"}`
                    : "Hand over"
                  : "Assign"}
            </Button>
          </div>
        }
      >
        <div className="px-5 py-2 flex flex-col gap-4">
          <div className="flex items-center gap-2 sticky top-2 z-10 bg-white">
            <SearchInput
              placeholder={peerMode ? "Search peers" : "Search assignee"}
              isKbd={false}
              className="flex-1"
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
            />
            {/* Branch / CF filters narrow the role pipeline; the peer list is
                already the server's branch + CF answer, so none apply. */}
            {!peerMode && (
              <FilterButton
                className="rounded-[0.75rem]"
                onClick={() => setShowFilter(!showFilter)}
              />
            )}
          </div>

          {showFilter && !peerMode && (
            <FilterSection
              showFilter={showFilter}
              filters={filters}
              onChange={setFilters}
            />
          )}

          {!selectedRole && (
            <RolesList
              setSelectedRole={setSelectedRole}
              kind={isChildActive ? "child" : "lead"}
              searchTerm={searchTerm}
              filters={filters}
              peerRow={
                peerAvailable
                  ? {
                      roleName: peerRoleName,
                      count: peersLoading ? null : peers.length,
                      onPick: () => setSelectedRole(PEER_ROLE),
                    }
                  : undefined
              }
            />
          )}
          {peerMode && (
            <PeerPickerList
              peers={peers}
              isLoading={peersLoading}
              error={
                peersError
                  ? peersErrorMessage ??
                    "Could not load your peers. The server refused the peer list — check that your role still has \"Transfer to peers\" on, then try again."
                  : null
              }
              selectedPeerId={selectedPeerId}
              onSelect={setSelectedPeerId}
              searchTerm={searchTerm}
            />
          )}
          {selectedRole && !peerMode && (
            <UsersList
              selectedRole={selectedRole}
              setSelectedRole={setSelectedRole}
              eligibility={isChildActive ? {} : eligibility}
              totalLeads={isChildActive ? 1 : totalLeads}
              kind={isChildActive ? "child" : "lead"}
              assignGateLead={assignGateFromLead}
              searchTerm={searchTerm}
              filters={filters}
            />
          )}
        </div>
      </RightSideSheet>

      <AssignLevelPopup
        open={levelPopupOpen}
        onClose={() => {
          setLevelPopupOpen(false);
          setAskPayload(null);
        }}
        ask={askPayload}
        isLoading={isBusy}
        onChooseLead={onLevelPopupChooseLead}
        onChooseChild={onLevelPopupChooseChild}
      />

      <CrossLevelSwapConfirmModal
        open={crossModalOpen}
        onClose={() => {
          setCrossModalOpen(false);
          setAskPayload(null);
          setCrossItems([]);
        }}
        onConfirm={onCrossModalConfirm}
        items={crossItems}
        isPending={assignLeads.isPending}
      />

      {sameRolePreview && (
        <SameRoleReplaceConfirmModal
          open={sameRoleModalOpen}
          onClose={() => {
            setSameRoleModalOpen(false);
            setSameRolePreview(null);
          }}
          onConfirm={onSameRoleReplaceConfirm}
          previousHolderName={sameRolePreview.peer.name}
          roleLabel={sameRolePreview.peer.role}
          incomingName={sameRolePreview.incoming.name}
          placementLabel={
            sameRolePreview.mode === "auto-child" ? "this deal" : "this lead"
          }
          isPending={assignLeads.isPending || assignChild.isPending}
        />
      )}

      {dealUserPreview && (
        <SameRoleReplaceConfirmModal
          open
          onClose={() => setDealUserPreview(null)}
          onConfirm={() => {
            runAssignChildren(dealUserPreview.childIds);
            closeAllModals();
          }}
          previousHolderName={[
            ...new Set([
              ...(dealUserPreview.replacingLeadHolder
                ? [dealUserPreview.replacingLeadHolder]
                : []),
              ...dealUserPreview.replacing.map((r) => r.peerName),
            ]),
          ].join(", ")}
          roleLabel={dealUserPreview.roleName}
          incomingName={dealUserPreview.incoming.name}
          placementLabel={
            dealUserPreview.replacing.length === 0
              ? "this lead"
              : dealUserPreview.replacing.length === 1 &&
                  !dealUserPreview.replacingLeadHolder
                ? `the deal "${dealUserPreview.replacing[0].childLabel}"`
                : "this lead's deals"
          }
          isPending={assignChild.isPending}
        />
      )}
    </>
  );
}
